import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter1() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 1</span>
          <h1>The Notification</h1>
          <p className="calculus-hero-sub">He had never left. He had never been seen. And his phone just changed everything.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Morning */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              The cabin sat at the end of a road that stopped being a road about two miles
              before it arrived. Trees on every side. No neighbors. No foot traffic. No reason
              for anyone to come this far unless they already knew what was here.
            </p>
            <p>
              What was here was a Half-Orc and the woman who raised him.
            </p>
            <p>
              He had been homeschooled. That was the word she used, though "home" implied
              something smaller than what she had built. The cabin had a library that
              spanned one entire wall. Biochemistry. Pharmacology. Neuroscience. Endocrinology.
              Organic chemistry. Behavioral psychology. And on the bottom shelf, where he
              had found them when he was nine, a collection of marketing textbooks she never
              explained and he never stopped reading.
            </p>
            <p>
              She taught him everything. How cortisol accumulates under sustained stress and
              why it takes ninety minutes to halve. How oxytocin forms between two nervous
              systems through repeated micro-exchanges. How dopamine is not the molecule of
              pleasure but the molecule of anticipation, and that the moment of maximum
              chemical engagement is not the answer but the space between the question and
              the answer. How adrenaline spikes in two minutes and clears in four. How
              serotonin holds the baseline of everything, and when it drops, nothing else
              works right.
            </p>
            <p>
              She also taught him about himself. Early. Before he could feel ashamed of it.
            </p>
            <p>
              "Your face will do something to people," she told him when he was six, sitting
              on the kitchen counter while she cooked. "Your tusks. Your eyes. The size you
              are going to be. Their bodies will react before their minds can. Adrenaline
              first. Then cortisol. It will happen in the first four hundred milliseconds,
              and there is nothing either of you can do to stop it."
            </p>
            <p>
              He had looked at her with those blazing orange eyes and asked, "Then what do
              I do?"
            </p>
            <p>
              "You give them the next four hundred milliseconds. That is where your voice
              lives. Lower register. Slower pace. Downward inflection at the end of your
              sentences. You will learn to speak in a frequency that tells their nervous
              system, before their brain catches up, that you are not the threat their
              chemistry thinks you are."
            </p>
            <p>
              He had practiced every day since. Twenty years of speaking softly in a cabin
              with no one to practice on but the woman who was never afraid of him to begin
              with.
            </p>
          </section>

          <Pulse />

          {/* The Notification */}
          <section className="calc-section reveal">
            <h2>The Notification</h2>
            <p>
              He had found the job listing three months ago. CEO. Remote-first company.
              The posting described a leadership role for someone who could "build culture
              across distance, inspire without proximity, and lead through communication
              rather than presence."
            </p>
            <p>
              He had read it seven times. Each time the dopamine spiked a little harder.
              This was written for someone like him. Someone who had never led through
              presence because presence had never been an option. Someone whose entire
              understanding of connection had been built through voice, through frequency,
              through the careful modulation of how words land in a nervous system that
              cannot see you.
            </p>
            <p>
              He applied. He did not tell her.
            </p>
            <p>
              The resume was honest. Education: private. Languages: four. Areas of study:
              neurochemistry, behavioral pharmacology, marketing theory, organizational
              communication. Experience: none. He had listed "none" and stared at it for
              ten minutes before hitting submit, because in every marketing textbook on
              that bottom shelf, the first rule was: do not lie about the product. Let
              the product speak. If it cannot speak for itself, it is not ready.
            </p>
            <p>
              He thought he was not ready. He applied anyway. Because the dopamine of
              "what if" was stronger than the cortisol of "what if they see me."
            </p>
            <p>
              The notification arrived on a Tuesday morning. He was upstairs. The sound
              was small, a chime from a laptop he had been given for his studies but had
              never used to contact anyone outside the cabin. He opened it.
            </p>
            <p>
              <em>Congratulations. After reviewing your application, we are pleased to
              offer you the position of Chief Executive Officer. Onboarding begins
              Monday. Please find attached your access credentials and onboarding
              schedule.</em>
            </p>
            <p>
              He read it four times. His hands were shaking. His dopamine was at a level
              his caretaker would have called "prediction error cascade," the state where
              reality deviates so far from expectation that the system floods with
              anticipatory chemistry and cannot stabilize. He was simultaneously elated
              and terrified. The curiosity and the fear were fighting for the same
              neurochemical real estate, and for the first time in his life, it was not
              a textbook exercise. It was happening inside him.
            </p>
            <p>
              He closed the laptop. He sat on the edge of his bed. He breathed the way
              she taught him. Four seconds in. Seven seconds hold. Eight seconds out.
              Vagal activation. Parasympathetic override. He did it six times before his
              hands stopped shaking.
            </p>
            <p>
              Then he went downstairs.
            </p>
          </section>

          <Pulse />

          {/* The Table */}
          <section className="calc-section reveal">
            <h2>The Table</h2>
            <p>
              The kitchen was empty.
            </p>
            <p>
              She was never not here. In twenty years, she had never once been gone when he
              came downstairs. She was the constant. The baseline. The serotonin of his
              entire existence. And the kitchen was empty.
            </p>
            <p>
              On the table: a watch. A cell phone. And a letter.
            </p>
            <p>
              The watch was simple. Silver. Analog. He had never worn one. She had always
              told him the time when he asked. He picked it up and put it on his left wrist,
              and the weight of it felt like a sentence he did not yet know how to read.
            </p>
            <p>
              The phone was already powered on. He pressed the screen and it unlocked to a
              home screen full of icons he did not recognize. Email. Slack. Calendar. Maps.
              Banking. Each one had a small red badge with a number on it. Notifications
              from a life he had not yet started, already accumulating.
            </p>
            <p>
              The letter was folded once. Her handwriting. He knew it from two decades of
              lesson plans and margin notes and the occasional "well done" written at the
              top of his work in green ink.
            </p>
            <p>
              He opened it.
            </p>
            <p>
              Two words. Her handwriting. Green ink.
            </p>

            <blockquote>
              <strong>Fly, bitch.</strong>
            </blockquote>

            <p>
              He laughed. It erupted out of him, loud and sudden and real, the kind of
              laugh that resets the entire neurochemical stack. Dopamine. Oxytocin. A sharp
              drop in cortisol. His eyes burned. He was laughing and almost crying and
              holding a letter from a woman who had spent twenty years teaching him the
              science of every molecule in his body and whose final lesson was two words
              that said: I did my job. Now go do yours.
            </p>
            <p>
              There was a knock at the door.
            </p>
          </section>

          <Pulse />

          {/* The Door */}
          <section className="calc-section reveal">
            <h2>The Door</h2>
            <p>
              He had never answered a door in his life. No one had ever knocked. The cabin
              did not receive visitors. The road did not invite them.
            </p>
            <p>
              He walked to the front door. His hand paused on the handle. His cortisol
              spiked, and he recognized it for what it was: the anticipation of being seen.
              Not the content of the threat. The prediction of it. His amygdala was
              rehearsing a moment that had not happened yet, and his body was responding to
              the rehearsal as though it were real.
            </p>
            <p>
              He breathed. He opened the door.
            </p>
            <p>
              A man stood on the porch. Immensely tall. Gaunt. Dressed in a black suit that
              looked like it had been pressed by someone who took pressing personally. His
              face was long, his expression was nonexistent, and his eyes looked down at
              the Half-Orc in the doorway with the absolute calm of a man who had seen
              stranger things than tusks before breakfast.
            </p>
            <p>
              "Your ride is here, Young Master."
            </p>
            <p>
              That was it. No flinch. No widened eyes. No involuntary step backward.
              No cortisol in his voice. No pitch shift. No compressed pause. The man's
              frequency was flat. Professional. Utterly, beautifully neutral.
            </p>
            <p>
              It was the first time in his life that another being had looked at his face
              and produced no threat response. Not warmth. Not fear. Neutral. And his own
              nervous system did something it had never done with a stranger before: it
              settled. The cortisol did not spike. The adrenaline did not fire. His body
              received the frequency of the man's voice and processed it as: safe. Not
              because the man was kind. Because the man was calm. And calm has a hertz
              signature that the amygdala reads before conscious thought can intervene.
            </p>
            <p>
              He stood in the doorway. The world outside was bright and impossibly
              wide and he did not move. His feet knew the cabin. They did not know
              the porch.
            </p>
            <p>
              The butler waited. He did not rush. He did not gesture. He simply stood
              beside the open car door with the patience of a man who understood that
              some thresholds take longer than others.
            </p>
            <p>
              "Whenever you are ready, Young Master." A pause. "You may wish to bring
              the phone. And the letter."
            </p>
            <p>
              He turned back. The phone was still on the table. The letter was beside
              it. He had almost walked out without either of them. His hands were
              shaking again. He picked up the phone, folded the letter into his pocket,
              and stepped through the front door of the only home he had ever known.
            </p>
            <p>
              The sunlight hit his face. The air smelled different outside. He got into
              a black car driven by a man whose name he did not know, heading toward a
              world he had studied for twenty years and never touched.
            </p>
          </section>

          <Pulse />

          {/* The Car */}
          <section className="calc-section reveal">
            <h2>The Car</h2>
            <p>
              The phone buzzed in his pocket before they reached the end of the road. He
              pulled it out and looked at the screen.
            </p>
            <p>
              Slack. The app icon had a purple hashtag. He tapped it.
            </p>
            <p>
              A feed opened. Hundreds of messages. A channel called #general. He scrolled.
            </p>
            <p>
              His picture was there. The headshot from his application. Tusks. Orange eyes.
              Black hair. It had been posted in the channel months ago when the company
              announced their new CEO. And beneath it, months of commentary.
            </p>
            <p>
              <em>"Has anyone actually MET this guy?"</em>
            </p>
            <p>
              <em>"Ghost CEO lmao"</em>
            </p>
            <p>
              <em>"I bet he's AI generated. No way that headshot is real."</em>
            </p>
            <p>
              <em>"Look at those teeth bro. This dude fires people for FUN."</em>
            </p>
            <p>
              <em>"Lol the Orc King. All hail."</em>
            </p>
            <p>
              He kept scrolling. The jokes spanned months. Memes. Photoshops. Someone had
              put his face on a movie villain. Someone else had made a "motivational poster"
              that said "LEADERSHIP: Because someone has to eat the interns." The channel
              treated him like a mythology. A legend. A thing to laugh about because it
              was easier than reckoning with the fact that their CEO had a face they had
              never seen on a person before.
            </p>
            <p>
              He understood. He was not angry. His caretaker had prepared him for this
              exact neurochemistry. Fear that cannot be expressed directly converts to
              humor. Cortisol that has no legitimate outlet finds release through laughter.
              The jokes were not cruelty. They were a coping mechanism for a nervous system
              confronted with something it had no category for. They were afraid, and this
              was how afraid looked when it wore a suit and sat behind a keyboard.
            </p>
            <p>
              He kept scrolling.
            </p>
            <p>
              And then he found her.
            </p>
          </section>

          <Pulse />

          {/* Loriann */}
          <section className="calc-section reveal">
            <h2>Loriann</h2>
            <p>
              Her name appeared in the feed like a wall. Every few threads, there she was.
              The same tone. The same authority. The same three sentences that never varied
              in temperature.
            </p>
            <p>
              <em>"Do I have to delete your threads again?"</em>
            </p>
            <p>
              <em>"You will treat our CEO with respect."</em>
            </p>
            <p>
              <em>"He is the reason you have a job."</em>
            </p>
            <p>
              No exclamation points. No emoji. No escalation. She never argued. She never
              engaged with the content of the jokes. She simply stated the boundary and
              moved on. Over and over. For months.
            </p>
            <p>
              She had never met him. She had never heard his voice. She had no evidence
              that he was kind, or competent, or worth defending. She had a photograph
              of a Half-Orc and a job title, and she had decided that was enough.
            </p>
            <p>
              His oxytocin spiked.
            </p>
            <p>
              Not from proximity. Not from touch. Not from a hallway nod or an elevator
              smile or any of the micro-exchanges his caretaker had told him build the
              chemistry of belonging. From text on a screen. From a stranger who had
              chosen to stand in front of him without ever standing next to him.
            </p>
            <p>
              That was the lesson his textbooks never taught. Connection does not require
              presence. Oxytocin does not require touch. Trust can form through words on
              a screen if the words carry weight. The medium is irrelevant. The chemistry
              is universal.
            </p>
            <p>
              He stared at her name for a long time while the trees blurred past the
              window.
            </p>
          </section>

          <Pulse />

          {/* Seven Words */}
          <section className="calc-section reveal">
            <h2>Seven Words</h2>
            <p>
              He opened the message composer. His thumbs hovered over the keyboard. He
              had never sent a message to anyone in his life. Every conversation he had
              ever had was spoken, face to face, with one person who already loved him.
            </p>
            <p>
              This would be his first act of leadership. His first frequency. Not spoken.
              Not heard. Typed into a box and delivered to four hundred people who had
              spent months building a mythology around his face.
            </p>
            <p>
              He thought about what his caretaker would say. She would say: the first
              signal sets the entire trajectory. The frequency of the first contact
              determines whether the system orients toward trust or toward threat. You
              do not get a second first frequency.
            </p>
            <p>
              He typed seven words.
            </p>

            <blockquote>
              <strong>"Thank you everyone for your kind words."</strong>
            </blockquote>

            <p>
              He pressed send.
            </p>
          </section>

          <Pulse />

          {/* The Silence */}
          <section className="calc-section reveal">
            <h2>The Silence</h2>
            <p>
              The feed went dead.
            </p>
            <p>
              Sixty messages a minute dropped to zero. Typing indicators appeared and
              disappeared. Three dots. Gone. Three dots. Gone. Someone started a response
              and deleted it. Someone else started and deleted. The channel held its
              breath.
            </p>
            <p>
              Four hundred people were sitting in home offices across time zones,
              staring at seven words from a man they had turned into a meme, realizing
              simultaneously that he had been in the channel the entire time. Every joke.
              Every photoshop. Every "Orc King" and "eats the interns." He saw all of it.
              He had always seen all of it.
            </p>
            <p>
              And his first words were not anger. Not discipline. Not "we need to talk."
              Seven words that could be read as grace or as the most terrifying sentence
              a guilty person has ever received, because the reader's own nervous system
              fills in the tone. And guilty nervous systems always fill in threat.
            </p>
            <p>
              In the absence of hertz, the reader's chemistry writes the frequency. Every
              person in that channel heard those seven words in the voice of their own
              conscience. And their conscience was not kind.
            </p>
            <p>
              The silence lasted eleven minutes.
            </p>
            <p>
              Then one person typed.
            </p>
          </section>

          <Pulse />

          {/* One Person */}
          <section className="calc-section reveal">
            <p>
              <em>"Thank you, Sir. It is nice to feel your presence. What shall we
              be working on today?"</em>
            </p>
            <p>
              Loriann. The only person in the company who did not flinch. Because she
              had nothing to flinch about. Her cortisol did not spike because her
              history with him was clean. She had treated him like a person when he was
              a photograph, and she was treating him like a person now.
            </p>
            <p>
              Every employee in that channel watched it happen. They watched the one
              person who had defended him become the only person he responded to through
              silence. The mathematics of it were visible to everyone. Loyalty before
              evidence gets rewarded. Fear after exposure gets silence.
            </p>
            <p>
              The car moved through the trees. The phone sat warm in his hand. Four
              hundred people waited. Loriann waited. His caretaker, wherever she was,
              probably already knew.
            </p>
            <p>
              He locked the phone. He put it in his pocket. He leaned back in the seat
              and watched the world he had never touched scroll past the window.
            </p>
            <p>
              He did not respond. Not yet. The silence was doing more work than any
              words could.
            </p>
            <p>
              His caretaker had taught him that too.
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 1</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The 400-Millisecond Window</span>
              <code>t = 0ms: visual stimulus &rarr; t = 400ms: amygdala threat assessment complete</code>
              <span className="calc-eq-desc">The human brain completes its initial threat assessment of a face in approximately 400 milliseconds. This process occurs in the amygdala before the prefrontal cortex can apply conscious reasoning. Appearance-based bias is not a choice. It is a neurochemical event that happens faster than thought.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Fear Converted to Humor</span>
              <code>if cortisol &gt; &theta;_discomfort AND social_context = group &rarr; output = humor</code>
              <span className="calc-eq-desc">Research in social psychology has established that humor serves as a cortisol regulation mechanism in group settings. When individuals experience discomfort they cannot express directly (such as fear of an authority figure), the nervous system redirects the stress response through laughter. The jokes were not cruelty. They were cortisol seeking an outlet.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Remote Oxytocin Formation</span>
              <code>if consistent_defense + no_personal_gain &rarr; OT forms in recipient (even without proximity)</code>
              <span className="calc-eq-desc">Studies on digital communication have shown that consistent prosocial behavior in text-based environments can trigger oxytocin responses in recipients. Loriann's repeated defense of a person she had never met produced a trust bond through text alone. Physical presence is not required for neurochemical bonding. Consistency is.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Silence Principle</span>
              <code>if sender_tone = absent &rarr; receiver_nervous_system supplies tone from own emotional state</code>
              <span className="calc-eq-desc">In the absence of vocal frequency (hertz), written communication is interpreted through the reader's current neurochemical state. A guilty reader will supply a threatening tone. A secure reader will supply a neutral one. Gnul's seven words carried no frequency. Every reader heard a different voice. Their own.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Vagal Breathing (4-7-8)</span>
              <code>inhale(4s) &rarr; hold(7s) &rarr; exhale(8s) &rarr; vagus nerve activation &rarr; cortisol attenuation</code>
              <span className="calc-eq-desc">The 4-7-8 breathing technique activates the vagus nerve, triggering the parasympathetic nervous system and attenuating the cortisol response. Extended exhalation (longer than inhalation) signals safety to the autonomic nervous system. This is a measurable, repeatable neurochemical intervention that requires no equipment and no training beyond the knowledge that it works.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Back to Gnul</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <a href="/gnul/chapter-2" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>Chapter 2: The Terminal &rarr;</a>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter1;
